import React, { useEffect, useState } from "react";
import API from "../../utils/API";
import { Button, Drawer, Field, Input, useToast } from "../../ui";

/**
 * Prefilled client edit (§3.8 — "the 5-field Update Client form dies").
 * Sends exactly the editable identity/contact fields; agent attribution,
 * uid, and provenance are server-denied for non-admins (updateOne2's
 * denyFields) and deliberately absent here for every tier.
 */
function ClientEditDrawer({ household, open, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && household) {
      const c = household.client;
      setForm({
        accountName: c.accountName || "",
        firstName: c.firstName || "",
        lastName: c.lastName || "",
        email: c.email || "",
        telephone: c.telephone || "",
        address: c.address || ""
      });
    }
  }, [open, household]);

  if (!open || !form) {
    return null;
  }

  const set = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const save = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error("First and last name are required.");
      return;
    }
    if (form.email && !/.+@.+\..+/.test(form.email)) {
      toast.error("That email address doesn't look right.");
      return;
    }
    setSaving(true);
    const payload = {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      accountName: form.accountName.trim(),
      telephone: form.telephone.trim(),
      address: form.address.trim()
    };
    // email is unique+sparse server-side: never send an empty string (it
    // would occupy the index); only send a real value.
    if (form.email.trim()) {
      payload.email = form.email.trim();
    }
    try {
      await API.updateClient(household.id, payload);
      toast.success("Client saved.");
      onSaved();
    } catch (err) {
      toast.error("The client couldn't be saved — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={`Edit ${household.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={save} loading={saving}>
            Save changes
          </Button>
        </>
      }
    >
      <Field
        label="Household display name"
        htmlFor="ce-account"
        help="Shown across the app (falls back to first + last name when empty)."
      >
        <Input id="ce-account" value={form.accountName} onChange={set("accountName")} />
      </Field>
      <Field label="First name" required htmlFor="ce-first">
        <Input
          id="ce-first"
          value={form.firstName}
          onChange={set("firstName")}
          autoComplete="off"
        />
      </Field>
      <Field label="Last name" required htmlFor="ce-last">
        <Input id="ce-last" value={form.lastName} onChange={set("lastName")} autoComplete="off" />
      </Field>
      <Field label="Email" htmlFor="ce-email">
        <Input
          id="ce-email"
          type="email"
          value={form.email}
          onChange={set("email")}
          autoComplete="off"
        />
      </Field>
      <Field label="Phone" htmlFor="ce-phone">
        <Input
          id="ce-phone"
          type="tel"
          value={form.telephone}
          onChange={set("telephone")}
          autoComplete="off"
        />
      </Field>
      <Field label="Address" htmlFor="ce-address">
        <Input id="ce-address" value={form.address} onChange={set("address")} autoComplete="off" />
      </Field>
    </Drawer>
  );
}

export default ClientEditDrawer;
